import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Suspense } from 'react';
import useSWR from 'swr';
import { defineMessages, useIntl } from 'react-intl';
import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { Typography } from '@packages/shared/src/components/Typography/Typography';
import type { CmsDataJsonApi } from '../../../interfaces/jsonapi';
import type { CmsData } from '../../../interfaces';
import { ServiceLayerLoader } from './ServiceLayerLoader';
import ServiceLayerFallback from './ServiceLayerFallback';
import ServiceLayerItem from './ServiceLayerItem';
import { fetcher } from '../../utils/fetcher';

/**
 * This component displays Service Layer Content on Product Detailview Pages
 *
 * @param serviceLayerId is the identifier to get content
 * @returns JSX.Element to be placed in PageLayout
 */

const serviceLayerMessages = defineMessages({
  errorLoading: {
    id: 'service.layer.error.failed.to.load',
    defaultMessage: 'Sorry, no content could be loaded. Please try again later',
  },
});

export type ServiceLayerProps = {
  serviceLayerId: string;
};

const ServiceLayerPrimary = ({ serviceLayerId }: ServiceLayerProps) => {
  const { locale } = useIntl();

  const {
    serviceLayer: { apiEndpoints },
  } = useConfig();

  // Fetch data from the Service Layer API using the useSWR hook and save it to dataPrimary and errorPrimary
  const { data: dataPrimary, error: errorPrimary } = useSWR<CmsDataJsonApi>(
    `${apiEndpoints.api}/${serviceLayerId}?locale=${locale}`,
    fetcher,
    {
      suspense: true, // Show a fallback UI while waiting for data to load
    },
  );

  // If there's an error or no data or an empty data array, throw the error
  if (errorPrimary || !dataPrimary || dataPrimary?.data?.length === 0) {
    throw errorPrimary;
  }

  return <ServiceLayerItem serviceLayerData={dataPrimary} />;
};

const ServiceLayerSecondary = ({ serviceLayerId }: ServiceLayerProps) => {
  const { locale } = useIntl();

  const {
    serviceLayer: { apiEndpoints },
  } = useConfig();

  // Fetch data from the Service Layer API using the useSWR hook and save it to dataSecondary and errorSecondary
  const { data: dataSecondary, error: errorSecondary } = useSWR<CmsData>(
    `${apiEndpoints.api}/${serviceLayerId}?locale=${locale}&useFallback=true`,
    fetcher,
    {
      suspense: true, // Show a fallback UI while waiting for data to load
    },
  );

  // If there's an error or no data or an empty paragraph array, throw the error
  if (errorSecondary || !dataSecondary || dataSecondary?.paragraph?.length === 0) {
    throw errorSecondary;
  }

  return <ServiceLayerFallback serviceLayerData={dataSecondary} />;
};

// ServiceLayerInitSecondary, which will be the fallback component for ServiceLayer
export const ServiceLayerInitSecondary = ({ serviceLayerId }: ServiceLayerProps): JSX.Element => {
  const { formatMessage } = useIntl();
  // Render an ErrorBoundary component to catch errors and display an error message
  // If an error occurs, render a Typography component with the error message
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="h4">{formatMessage(serviceLayerMessages.errorLoading)}</Typography>
      }
    >
      <Suspense fallback={<ServiceLayerLoader />}>
        <ServiceLayerSecondary serviceLayerId={serviceLayerId} />
      </Suspense>
    </ErrorBoundary>
  );
};

export const ServiceLayer = ({ serviceLayerId }: ServiceLayerProps): JSX.Element => (
  // Render an ErrorBoundary component to catch errors and display the fallback component ServiceLayerInitSecondary
  <ErrorBoundary fallback={<ServiceLayerInitSecondary serviceLayerId={serviceLayerId} />}>
    <Suspense fallback={<ServiceLayerLoader />}>
      <ServiceLayerPrimary serviceLayerId={serviceLayerId} />
    </Suspense>
  </ErrorBoundary>
);
